import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BottomNav } from '../../components/feature/BottomNav';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { TicketModal } from '../mypage/components/favorites/TicketModal';
import type { PurchasedTicket } from '../mypage/components/FavoritesTab';

type ScanMode = 'NFC' | 'QR';
type PageTab = 'scan' | 'tickets';

// ── チケットカード ──────────────────────────────────────────
function ScanTicketCard({
  ticket,
  onOpen,
}: {
  ticket: PurchasedTicket;
  onOpen: () => void;
}) {
  const isCheckedIn = ticket.payment_status === 'checked_in';
  const isValid = ticket.payment_status === 'completed';

  const statusColor = isCheckedIn ? '#10b981' : isValid ? '#f59e0b' : '#ef4444';
  const statusLabel = isCheckedIn ? '入場済み' : isValid ? '有効' : '無効';

  // 残り日数
  const daysLeft = (() => {
    if (!ticket.eventDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ev = new Date(ticket.eventDate);
    ev.setHours(0, 0, 0, 0);
    return Math.floor((ev.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-xl border overflow-hidden cursor-pointer group transition-all duration-300 hover:scale-[1.01]"
      style={{
        borderColor: `${statusColor}35`,
        background: `linear-gradient(135deg, ${statusColor}08, rgba(0,0,0,0.6))`,
        boxShadow: `0 0 20px ${statusColor}10`,
      }}
      onClick={onOpen}
    >
      {/* ホバー時グロー */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ boxShadow: `inset 0 0 30px ${statusColor}15` }}
      />

      <div className="flex items-center gap-3 p-3">
        {/* サムネイル */}
        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 relative">
          {ticket.eventThumbnail ? (
            <img
              src={ticket.eventThumbnail}
              alt={ticket.eventTitle}
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: `${statusColor}15` }}
            >
              <i className="ri-calendar-event-line text-2xl" style={{ color: statusColor }} />
            </div>
          )}
          {isCheckedIn && (
            <div className="absolute inset-0 bg-emerald-500/40 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-emerald-300 text-xl" />
            </div>
          )}
        </div>

        {/* 情報 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <span className="text-white text-sm font-bold truncate">{ticket.eventTitle ?? '—'}</span>
            {daysLeft !== null && daysLeft >= 0 && !isCheckedIn && (
              <span
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0"
                style={{
                  background: daysLeft === 0 ? 'rgba(239,68,68,0.2)' : daysLeft <= 3 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                  border: `1px solid ${daysLeft <= 3 ? 'rgba(239,68,68,0.4)' : 'rgba(245,158,11,0.4)'}`,
                  color: daysLeft <= 3 ? '#ef4444' : '#f59e0b',
                }}
              >
                {daysLeft === 0 ? 'TODAY' : `あと${daysLeft}日`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <i className="ri-calendar-line text-[10px]" />
            <span>{ticket.eventDate ?? '—'}</span>
            <span className="text-gray-700">·</span>
            <i className="ri-map-pin-line text-[10px]" />
            <span className="truncate">{ticket.eventVenue ?? '—'}</span>
          </div>
        </div>

        {/* 右側 */}
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <div
            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
            style={{
              background: `${statusColor}20`,
              border: `1px solid ${statusColor}40`,
              color: statusColor,
            }}
          >
            <i className={`${isCheckedIn ? 'ri-checkbox-circle-fill' : isValid ? 'ri-shield-check-line' : 'ri-close-circle-line'} text-xs`} />
            {statusLabel}
          </div>
          <button
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{
              background: `${statusColor}15`,
              border: `1px solid ${statusColor}35`,
              color: statusColor,
            }}
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
          >
            <i className="ri-qr-code-line text-xs" />
            QR表示
          </button>
        </div>
      </div>

      {/* 底部ライン */}
      <div
        className="h-0.5 w-full"
        style={{ background: `linear-gradient(90deg, transparent, ${statusColor}50, transparent)` }}
      />
    </motion.div>
  );
}

// ── メインページ ──────────────────────────────────────────
export default function ScanPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pageTab, setPageTab] = useState<PageTab>('scan');
  const [mode, setMode] = useState<ScanMode>('NFC');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [glitchActive, setGlitchActive] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // チケット関連
  const [tickets, setTickets] = useState<PurchasedTicket[]>([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<PurchasedTicket | null>(null);
  const [ticketFilter, setTicketFilter] = useState<'all' | 'unused' | 'used'>('unused');

  // グリッチエフェクト
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 200);
    }, 4000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // チケット取得
  useEffect(() => {
    if (!user || pageTab !== 'tickets') return;

    const fetchTickets = async () => {
      setIsLoadingTickets(true);
      try {
        const { data, error } = await supabase
          .from('event_reservations')
          .select('id, event_id, ticket_count, total_amount, payment_status, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        if (!data || data.length === 0) {
          setTickets([]);
          return;
        }

        const eventIds = [...new Set(data.map((r) => r.event_id))];
        const { data: eventsData } = await supabase
          .from('events')
          .select('id, title, date, time, venue, thumbnail, color')
          .in('id', eventIds);

        const eventMap = new Map((eventsData || []).map((e) => [e.id, e]));

        const enriched: PurchasedTicket[] = data.map((r) => {
          const ev = eventMap.get(r.event_id);
          return {
            ...r,
            eventTitle: ev?.title,
            eventDate: ev?.date,
            eventTime: ev?.time,
            eventVenue: ev?.venue,
            eventThumbnail: ev?.thumbnail,
            eventColor: ev?.color || '#f59e0b',
          };
        });

        setTickets(enriched);
      } catch (err) {
        console.error('Error fetching tickets:', err);
      } finally {
        setIsLoadingTickets(false);
      }
    };

    fetchTickets();
  }, [user, pageTab]);

  const handleNFCScan = async () => {
    if (!user) { navigate('/login'); return; }
    setScanResult(null);
    setScanError(null);
    setScanning(true);

    if (!('NDEFReader' in window)) {
      setScanError('このデバイスはNFCに対応していません。QRモードをお試しください。');
      setScanning(false);
      return;
    }

    try {
      const ndef = new (window as any).NDEFReader();
      await ndef.scan();
      ndef.addEventListener('reading', ({ message }: any) => {
        for (const record of message.records) {
          if (record.recordType === 'url') {
            const decoder = new TextDecoder();
            const url = decoder.decode(record.data);
            setScanResult(url);
            setScanning(false);
            setTimeout(() => {
              const path = url.replace(window.location.origin, '');
              navigate(path);
            }, 1200);
            return;
          }
        }
        setScanError('NFCタグのデータを読み取れませんでした。');
        setScanning(false);
      });
    } catch (err: any) {
      setScanError(`NFCスキャンエラー: ${err.message || '不明なエラー'}`);
      setScanning(false);
    }
  };

  const handleQRScan = () => {
    if (!user) { navigate('/login'); return; }
    setScanResult(null);
    setScanError(null);
    navigate('/nfc');
  };

  const cancelScan = () => {
    setScanning(false);
    setScanResult(null);
    setScanError(null);
  };

  // フィルタリング済みチケット
  const filteredTickets = tickets.filter((t) => {
    if (ticketFilter === 'unused') return t.payment_status !== 'checked_in';
    if (ticketFilter === 'used') return t.payment_status === 'checked_in';
    return true;
  }).sort((a, b) => {
    if (ticketFilter !== 'used') {
      const da = a.eventDate ? new Date(a.eventDate).getTime() : Infinity;
      const db = b.eventDate ? new Date(b.eventDate).getTime() : Infinity;
      return da - db;
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const unusedCount = tickets.filter(t => t.payment_status !== 'checked_in').length;
  const usedCount = tickets.filter(t => t.payment_status === 'checked_in').length;

  const handleCheckedIn = (ticketId: string) => {
    setTickets(prev =>
      prev.map(t => t.id === ticketId ? { ...t, payment_status: 'checked_in' } : t)
    );
  };

  return (
    <div className="bg-black min-h-screen text-white pb-28 relative overflow-hidden">
      <style>{`
        @keyframes scanLine {
          0% { top: 0%; opacity: 0.8; }
          50% { opacity: 1; }
          100% { top: 100%; opacity: 0.8; }
        }
        @keyframes radarSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes glitch {
          0%   { clip-path: inset(0 0 95% 0); transform: translate(-4px, 0); }
          20%  { clip-path: inset(30% 0 50% 0); transform: translate(4px, 0); }
          40%  { clip-path: inset(60% 0 20% 0); transform: translate(-2px, 0); }
          60%  { clip-path: inset(80% 0 5% 0); transform: translate(3px, 0); }
          80%  { clip-path: inset(10% 0 70% 0); transform: translate(-3px, 0); }
          100% { clip-path: inset(0 0 95% 0); transform: translate(0, 0); }
        }
        @keyframes neonPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes cornerBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .scan-line {
          position: absolute;
          left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #00d9ff, transparent);
          box-shadow: 0 0 12px #00d9ff, 0 0 24px #00d9ff80;
          animation: scanLine 2s linear infinite;
        }
        .radar-spin { animation: radarSpin 3s linear infinite; }
        .glitch-text { animation: glitch 0.2s steps(1) forwards; }
        .neon-pulse { animation: neonPulse 2s ease-in-out infinite; }
        .corner-blink { animation: cornerBlink 1.5s ease-in-out infinite; }
      `}</style>

      {/* 背景オーブ */}
      <motion.div
        className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] bg-cyan-900/20 rounded-full blur-3xl pointer-events-none"
        animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-[-200px] right-[-200px] w-[500px] h-[500px] bg-pink-900/20 rounded-full blur-3xl pointer-events-none"
        animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 max-w-lg mx-auto px-4">
        {/* ヘッダー */}
        <div className="pt-10 pb-4">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-2 h-2 rounded-full bg-cyan-400 neon-pulse" />
              <span className="text-xs tracking-[0.3em] text-cyan-400 font-mono uppercase">System Online</span>
            </div>
            <h1
              className={`text-4xl font-black tracking-widest text-white ${glitchActive ? 'glitch-text' : ''}`}
              style={{ textShadow: '0 0 20px rgba(0,217,255,0.6)' }}
            >
              SCAN
            </h1>
            <p className="text-gray-500 text-xs tracking-widest mt-1 font-mono">
              NFC TAP / QR CAMERA — FIELD SCANNER v2.0
            </p>
          </motion.div>
        </div>

        {/* ページタブ（SCAN / MY TICKETS） */}
        <motion.div
          className="flex gap-2 mb-6 bg-gray-900/60 border border-gray-700/50 rounded-xl p-1 backdrop-blur-xl"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <button
            onClick={() => setPageTab('scan')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap ${
              pageTab === 'scan'
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 shadow-[0_0_16px_rgba(0,217,255,0.3)]'
                : 'text-gray-500 hover:text-gray-300 border border-transparent'
            }`}
          >
            <i className="ri-scan-2-line text-lg" />
            SCAN
          </button>
          <button
            onClick={() => {
              if (!user) { navigate('/login'); return; }
              setPageTab('tickets');
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap ${
              pageTab === 'tickets'
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50 shadow-[0_0_16px_rgba(245,158,11,0.3)]'
                : 'text-gray-500 hover:text-gray-300 border border-transparent'
            }`}
          >
            <i className="ri-ticket-2-line text-lg" />
            MY TICKETS
            {unusedCount > 0 && pageTab !== 'tickets' && (
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-black text-[10px] font-black">
                {unusedCount}
              </span>
            )}
          </button>
        </motion.div>

        {/* ── SCAN タブ ── */}
        <AnimatePresence mode="wait">
          {pageTab === 'scan' && (
            <motion.div
              key="scan-tab"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              {/* NFC / QR モード切り替え */}
              <div className="flex gap-2 mb-8 bg-gray-900/60 border border-gray-700/50 rounded-xl p-1 backdrop-blur-xl">
                {(['NFC', 'QR'] as ScanMode[]).map((m) => (
                  <button
                    key={m}
                    onClick={() => { setMode(m); cancelScan(); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      mode === m
                        ? m === 'NFC'
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 shadow-[0_0_16px_rgba(0,217,255,0.3)]'
                          : 'bg-pink-500/20 text-pink-400 border border-pink-500/50 shadow-[0_0_16px_rgba(255,0,110,0.3)]'
                        : 'text-gray-500 hover:text-gray-300 border border-transparent'
                    }`}
                  >
                    <i className={`${m === 'NFC' ? 'ri-nfc-fill' : 'ri-qr-code-line'} text-lg`} />
                    {m === 'NFC' ? '📡 NFC TAP' : '📷 QR CAMERA'}
                  </button>
                ))}
              </div>

              {/* スキャナーエリア */}
              <motion.div className="relative mb-8" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
                <div
                  className={`relative rounded-2xl border-2 overflow-hidden backdrop-blur-xl ${
                    mode === 'NFC'
                      ? 'border-cyan-500/40 bg-gradient-to-br from-cyan-950/40 to-gray-900/60'
                      : 'border-pink-500/40 bg-gradient-to-br from-pink-950/40 to-gray-900/60'
                  }`}
                  style={{
                    boxShadow: mode === 'NFC'
                      ? '0 0 40px rgba(0,217,255,0.15), inset 0 0 40px rgba(0,217,255,0.05)'
                      : '0 0 40px rgba(255,0,110,0.15), inset 0 0 40px rgba(255,0,110,0.05)',
                  }}
                >
                  {['top-3 left-3 border-t-2 border-l-2', 'top-3 right-3 border-t-2 border-r-2', 'bottom-3 left-3 border-b-2 border-l-2', 'bottom-3 right-3 border-b-2 border-r-2'].map((cls, i) => (
                    <div key={i} className={`absolute w-6 h-6 corner-blink ${cls} ${mode === 'NFC' ? 'border-cyan-400' : 'border-pink-400'}`} />
                  ))}

                  <div className="flex flex-col items-center justify-center py-16 px-8">
                    <AnimatePresence mode="wait">
                      {scanning ? (
                        <motion.div key="scanning" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className="flex flex-col items-center gap-6">
                          <div className="relative w-32 h-32">
                            <div className={`absolute inset-0 rounded-full border-2 ${mode === 'NFC' ? 'border-cyan-500/30' : 'border-pink-500/30'}`} />
                            <div className={`absolute inset-4 rounded-full border ${mode === 'NFC' ? 'border-cyan-500/20' : 'border-pink-500/20'}`} />
                            <div className={`absolute inset-8 rounded-full border ${mode === 'NFC' ? 'border-cyan-500/40' : 'border-pink-500/40'}`} />
                            <div className={`absolute inset-0 rounded-full border-t-2 radar-spin ${mode === 'NFC' ? 'border-cyan-400' : 'border-pink-400'}`} style={{ boxShadow: mode === 'NFC' ? '0 0 12px rgba(0,217,255,0.8)' : '0 0 12px rgba(255,0,110,0.8)' }} />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <i className={`${mode === 'NFC' ? 'ri-nfc-fill' : 'ri-qr-code-line'} text-3xl ${mode === 'NFC' ? 'text-cyan-400' : 'text-pink-400'}`} />
                            </div>
                          </div>
                          <div className="text-center">
                            <motion.p className={`text-lg font-bold tracking-widest ${mode === 'NFC' ? 'text-cyan-400' : 'text-pink-400'}`} animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>
                              {mode === 'NFC' ? 'SCANNING NFC...' : 'SCANNING QR...'}
                            </motion.p>
                            <p className="text-gray-500 text-xs mt-2 font-mono">{mode === 'NFC' ? 'デバイスをNFCタグに近づけてください' : 'QRコードをカメラに向けてください'}</p>
                          </div>
                          <button onClick={cancelScan} className="px-6 py-2 rounded-lg border border-gray-600 text-gray-400 text-sm hover:border-gray-400 hover:text-white transition-all cursor-pointer whitespace-nowrap">キャンセル</button>
                        </motion.div>
                      ) : scanResult ? (
                        <motion.div key="result" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4 text-center">
                          <div className="w-16 h-16 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center">
                            <i className="ri-check-line text-3xl text-green-400" />
                          </div>
                          <p className="text-green-400 font-bold tracking-widest text-lg">SCAN SUCCESS</p>
                          <p className="text-gray-400 text-xs font-mono break-all">{scanResult}</p>
                          <p className="text-gray-500 text-xs">リダイレクト中...</p>
                        </motion.div>
                      ) : scanError ? (
                        <motion.div key="error" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4 text-center">
                          <div className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500/50 flex items-center justify-center">
                            <i className="ri-error-warning-line text-3xl text-red-400" />
                          </div>
                          <p className="text-red-400 font-bold tracking-widest">SCAN ERROR</p>
                          <p className="text-gray-400 text-xs font-mono">{scanError}</p>
                          <button onClick={cancelScan} className="px-6 py-2 rounded-lg border border-gray-600 text-gray-400 text-sm hover:border-gray-400 hover:text-white transition-all cursor-pointer whitespace-nowrap">リトライ</button>
                        </motion.div>
                      ) : (
                        <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-6 text-center">
                          <div className="relative">
                            <motion.div
                              className={`w-28 h-28 rounded-full flex items-center justify-center border-2 ${mode === 'NFC' ? 'border-cyan-500/50 bg-cyan-500/10' : 'border-pink-500/50 bg-pink-500/10'}`}
                              animate={{ boxShadow: mode === 'NFC' ? ['0 0 20px rgba(0,217,255,0.3)', '0 0 40px rgba(0,217,255,0.6)', '0 0 20px rgba(0,217,255,0.3)'] : ['0 0 20px rgba(255,0,110,0.3)', '0 0 40px rgba(255,0,110,0.6)', '0 0 20px rgba(255,0,110,0.3)'] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            >
                              <i className={`${mode === 'NFC' ? 'ri-nfc-fill' : 'ri-qr-scan-2-line'} text-5xl ${mode === 'NFC' ? 'text-cyan-400' : 'text-pink-400'}`} />
                            </motion.div>
                            {[1, 2].map((ring) => (
                              <motion.div key={ring} className={`absolute inset-0 rounded-full border ${mode === 'NFC' ? 'border-cyan-500/20' : 'border-pink-500/20'}`} animate={{ scale: [1, 1.4 + ring * 0.3], opacity: [0.6, 0] }} transition={{ duration: 2, repeat: Infinity, delay: ring * 0.5 }} />
                            ))}
                          </div>
                          <div>
                            <p className={`text-xl font-bold tracking-widest mb-2 ${mode === 'NFC' ? 'text-cyan-400' : 'text-pink-400'}`}>{mode === 'NFC' ? 'TAP TO SCAN' : 'OPEN CAMERA'}</p>
                            <p className="text-gray-500 text-xs font-mono leading-relaxed">{mode === 'NFC' ? '会場のNFCタグにデバイスをタップして\nチェックインしてください' : 'QRコードをカメラで読み取って\nチェックインしてください'}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    {scanning && (
                      <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-2xl">
                        <div className="scan-line" />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {!scanning && !scanResult && (
                <motion.button
                  onClick={mode === 'NFC' ? handleNFCScan : handleQRScan}
                  className={`w-full py-5 rounded-xl font-black text-lg tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap border-2 ${mode === 'NFC' ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400 hover:bg-cyan-500/30' : 'bg-pink-500/20 border-pink-500 text-pink-400 hover:bg-pink-500/30'}`}
                  style={{ boxShadow: mode === 'NFC' ? '0 0 30px rgba(0,217,255,0.4)' : '0 0 30px rgba(255,0,110,0.4)' }}
                  whileTap={{ scale: 0.97 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <span className="flex items-center justify-center gap-3">
                    <i className={`${mode === 'NFC' ? 'ri-nfc-fill' : 'ri-qr-scan-2-line'} text-2xl`} />
                    {mode === 'NFC' ? 'START NFC SCAN' : 'OPEN QR SCANNER'}
                  </span>
                </motion.button>
              )}

              {/* 使い方ガイド */}
              <motion.div className="mt-8 bg-gray-900/40 border border-gray-700/40 rounded-xl p-5 backdrop-blur-xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-information-line text-gray-500" />
                  <span className="text-xs tracking-widest text-gray-500 font-mono uppercase">How to Use</span>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-map-pin-2-line', color: 'text-cyan-400', text: '会場に設置されたNFCタグまたはQRコードを探す' },
                    { icon: 'ri-nfc-fill', color: 'text-pink-400', text: 'NFCはデバイスをタップ、QRはカメラで読み取る' },
                    { icon: 'ri-trophy-line', color: 'text-yellow-400', text: 'チェックインでEXPとDepthを獲得！' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${item.color}`}>
                        <i className={`${item.icon} text-sm`} />
                      </div>
                      <p className="text-gray-400 text-xs font-mono leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* ── MY TICKETS タブ ── */}
          {pageTab === 'tickets' && (
            <motion.div
              key="tickets-tab"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.25 }}
            >
              {/* サマリー */}
              {tickets.length > 0 && (
                <div className="flex items-center gap-4 mb-4 px-1 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-ticket-2-line text-amber-400 text-xs" />
                    <span className="text-gray-400 text-xs">
                      合計 <span className="text-amber-400 font-bold">{tickets.reduce((s, t) => s + t.ticket_count, 0)}</span> 枚
                    </span>
                  </div>
                  <div className="w-px h-3 bg-white/10" />
                  <div className="flex items-center gap-1.5">
                    <i className="ri-checkbox-circle-line text-emerald-400 text-xs" />
                    <span className="text-gray-400 text-xs">
                      来場済み <span className="text-emerald-400 font-bold">{usedCount}</span> 件
                    </span>
                  </div>
                  <div className="w-px h-3 bg-white/10" />
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line text-amber-400 text-xs" />
                    <span className="text-gray-400 text-xs">
                      未使用 <span className="text-amber-400 font-bold">{unusedCount}</span> 件
                    </span>
                  </div>
                </div>
              )}

              {/* フィルター */}
              <div className="flex items-center gap-1.5 mb-5 p-1 rounded-full bg-white/5 border border-white/10 w-fit">
                {([
                  { key: 'unused', label: '未使用', icon: 'ri-ticket-line', count: unusedCount, color: '#f59e0b' },
                  { key: 'used', label: '来場済み', icon: 'ri-checkbox-circle-line', count: usedCount, color: '#10b981' },
                  { key: 'all', label: 'すべて', icon: 'ri-list-check', count: tickets.length, color: '#9ca3af' },
                ] as const).map(({ key, label, icon, count, color }) => {
                  const isActive = ticketFilter === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setTicketFilter(key)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap"
                      style={{
                        background: isActive ? `${color}20` : 'transparent',
                        border: isActive ? `1px solid ${color}50` : '1px solid transparent',
                        color: isActive ? color : '#6b7280',
                      }}
                    >
                      <i className={`${icon} text-xs`} />
                      {label}
                      <span
                        className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                        style={{
                          background: isActive ? `${color}25` : 'rgba(255,255,255,0.05)',
                          color: isActive ? color : '#4b5563',
                        }}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* チケットリスト */}
              {isLoadingTickets ? (
                <div className="flex items-center justify-center py-16">
                  <motion.i
                    className="ri-loader-4-line text-3xl text-amber-400"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                </div>
              ) : filteredTickets.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center py-16 gap-4"
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}
                  >
                    <i className="ri-ticket-line text-amber-400 text-2xl" />
                  </div>
                  <p className="text-gray-500 text-sm tracking-wider">
                    {tickets.length === 0
                      ? '購入済みチケットがありません'
                      : ticketFilter === 'unused'
                        ? '未使用のチケットはありません'
                        : '来場済みのチケットはありません'}
                  </p>
                  {tickets.length === 0 && (
                    <button
                      onClick={() => navigate('/explore')}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold tracking-wider cursor-pointer whitespace-nowrap transition-all duration-200 hover:scale-105"
                      style={{
                        background: 'rgba(245,158,11,0.15)',
                        border: '1px solid rgba(245,158,11,0.4)',
                        color: '#f59e0b',
                      }}
                    >
                      <i className="ri-compass-3-line" />
                      イベントを探す
                    </button>
                  )}
                </motion.div>
              ) : (
                <div className="space-y-3">
                  {/* 未使用チケットの注意書き */}
                  {ticketFilter !== 'used' && filteredTickets.some(t => t.payment_status !== 'checked_in') && (
                    <div
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-amber-400/80 mb-2"
                      style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}
                    >
                      <i className="ri-information-line flex-shrink-0" />
                      <span>チケットをタップするとQRコードを表示できます。会場スタッフに提示してください。</span>
                    </div>
                  )}

                  {filteredTickets.map((ticket) => (
                    <ScanTicketCard
                      key={ticket.id}
                      ticket={ticket}
                      onOpen={() => setSelectedTicket(ticket)}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* チケットモーダル */}
      {selectedTicket && (
        <TicketModal
          ticket={selectedTicket}
          isOpen={true}
          onClose={() => setSelectedTicket(null)}
          onCheckedIn={handleCheckedIn}
        />
      )}

      <BottomNav />
    </div>
  );
}
